import { useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { HeaderSteps } from './HeaderSteps'
import { LogoutMessage } from './LogoutMessage'
import { Steps } from './Steps'

import { Icons } from '@/components/icons'
import { Card, CardBody, CardHeader, useDisclosure } from '@/components/nextui'
import { useAuthHook } from '@/hooks/useAuthHook'
import { Forms } from '@/types/forms'

interface HeaderProps {
    progress: number
    steps: Forms[] | undefined
    currentStep: number
    setCurrentStep: (step: number) => void
    setLoadingInscription: (loading: boolean) => void
    isInactiveInscription?: boolean
}
export const Header = ({
    progress,
    steps,
    currentStep,
    setCurrentStep,
    setLoadingInscription,
    isInactiveInscription = false
}: HeaderProps): JSX.Element => {
    const { t } = useTranslation()
    const { logout, firstName, lastName } = useAuthHook()
    const { isOpen, onOpen, onClose } = useDisclosure()

    // Create full name from firstName and lastName
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : ''

    const ContactSupport = (): void => {
        window.open(
            `https://journey.debmedia.com/ubEcDjuXFSeHgd9V3oxI/16`,
            '_blank'
        )
    }

    const stepsContainerRef = useRef<HTMLDivElement | null>(null) // Create a ref for the steps container
    const NextSteps = (): void => {
        if (stepsContainerRef.current) {
            stepsContainerRef.current.scrollBy({
                left: stepsContainerRef.current.clientWidth, // Scroll by the width of the container
                behavior: 'smooth' // Smooth scrolling
            })
        }
    }

    const PreviousSteps = (): void => {
        if (stepsContainerRef.current) {
            stepsContainerRef.current.scrollBy({
                left: -stepsContainerRef.current.clientWidth, // Scroll back by the width of the container
                behavior: 'smooth' // Smooth scrolling
            })
        }
    }

    const logoutInfo = (): void => {
        onClose()
        logout()
    }

    return (
        <Card className="flex w-full shadow-xl">
            <LogoutMessage
                isOpen={isOpen}
                onClose={logoutInfo}
            />
            <CardHeader>
                <HeaderSteps
                    progress={progress.toString()}
                    informationText={t('headerSteps.information')}
                    logoutText={t('headerSteps.logout')}
                    supportText={t('headerSteps.support')}
                    fullName={fullName}
                    logout={onOpen}
                    support={ContactSupport}
                    setLoadingInscription={setLoadingInscription}
                />
            </CardHeader>
            <CardBody className="flex flex-row justify-center gap-4 items-center md:py-0">
                <Icons.AngleLeft
                    className="w-4 h-4 md:hidden"
                    onClick={PreviousSteps}
                />
                <div
                    ref={stepsContainerRef}
                    className="flex overflow-x-auto "
                >
                    {steps && (
                        <Steps
                            activeStep={currentStep}
                            onStepChange={setCurrentStep}
                            isInactiveInscription={isInactiveInscription}
                        >
                            {steps.map((step, index) => (
                                <Steps.Step
                                    key={'step-' + index}
                                    title={step.formName}
                                    id={index + 1}
                                    isCompleted={step.completed}
                                />
                            ))}
                        </Steps>
                    )}
                </div>
                <Icons.AngleRight
                    className="w-4 h-4 md:hidden"
                    onClick={NextSteps}
                />
            </CardBody>
        </Card>
    )
}
